//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

import {
  saveImageOnCloud,
  saveImageOnCloudB64,
  saveVideoOnCloud,
  saveAudioOnCloud,
} from './db';

import { fetchWithTrace } from './fetchWithTrace';

import thumbnail from '../components/assets/image/alekhine_thumbnail.jpg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const getUserId = () => localStorage.getItem('userId') || 'guest';
const KEY = (projectId) => `artifacts:${getUserId()}:${projectId}`;
const dropLocal = (projectId) => localStorage.removeItem(KEY(projectId));

const ensureLocal = (projectId) => {
  const raw = localStorage.getItem(KEY(projectId));
  if (!raw) {
    const empty = { image: [], video: [], audio: [] };
    localStorage.setItem(KEY(projectId), JSON.stringify(empty));
    return empty;
  }
  try {
    const val = JSON.parse(raw);
    return { image: val.image || [], video: val.video || [], audio: val.audio || [] };
  } catch {
    const empty = { image: [], video: [], audio: [] };
    localStorage.setItem(KEY(projectId), JSON.stringify(empty));
    return empty;
  }
};


const groupList = (arr = []) =>
  arr.reduce((acc, a) => {
    const t = a?.type || 'image';
    (acc[t] ||= []).push(a);
    return acc;
  }, { image: [], video: [], audio: [] });

export async function listArtifacts(projectId) {
  if (!projectId) return { image: [], video: [], audio: [] };

  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects/${projectId}/artifacts`, {
      credentials: 'include',
    });

    if (data?.images || data?.videos || data?.audios) {
      return {
        image: data.images || [],
        video: data.videos || [],
        audio: data.audios || [],
      };
    }
    if (data?.image || data?.video || data?.audio) return data;
    if (Array.isArray(data)) return groupList(data);
    return { image: [], video: [], audio: [] };
  } catch {
    // fallback to local cache
    return ensureLocal(projectId);
  }
}

export async function saveArtifact(projectId, artifact) {
  if (!projectId) throw new Error('saveArtifact: missing projectId');

  const type = artifact.type || 'image';
  let outputUrl = artifact.outputUrl;

  // 1) Upload media to your cloud if needed
  const stamp = Date.now();

  const localId = `local:${projectId}:${type}:${stamp}:${Math.random().toString(36).slice(2, 8)}`;

  if (type === 'image') {
    const imageId = `${projectId}_img_${stamp}`;
    if (outputUrl?.startsWith('data:')) {
      // data URL → strip header → send base64 to backend
      const b64 = outputUrl.split(',')[1];
      outputUrl = await saveImageOnCloudB64(b64, imageId, projectId, 'playground');
    } else if (outputUrl) {
      outputUrl = await saveImageOnCloud(outputUrl, imageId, projectId, 'playground');
    }
  } else if (type === 'video') {
    if (outputUrl) {
      // Optimization: If it's already on GCS (e.g. from Veo), don't re-upload/duplicate it.
      const isAlreadyGcs = outputUrl.includes('storage.googleapis.com') || outputUrl.includes('storage.cloud.google.com');

      if (!isAlreadyGcs) {
        const videoId = `${projectId}_vid_${stamp}`;
        outputUrl = await saveVideoOnCloud(outputUrl, videoId, projectId, 'playground');
      }
    }
  } else if (type === 'audio') {
    // saveAudioOnCloud(fileOrUrl, name) – adapt if your call signature differs
    if (artifact.audioFile) {
      const audioId = `${projectId}_aud_${stamp}`;
      outputUrl = await saveAudioOnCloud(artifact.audioFile, audioId, projectId, 'playground');
    }
  }

  // 2) Persist metadata to backend
  const payload = {
    _id: artifact._id || localId,
    type,
    title: artifact.title,
    prompt: artifact.prompt,
    inputImageUrl: artifact.inputImageUrl || undefined,
    inputImages: artifact.inputImages || undefined, // New: save multi-image input
    outputUrl,
    model: artifact.model,
    createdAt: artifact.createdAt || new Date().toISOString(),
    thumbnailUrl: artifact.inputImageUrl || artifact.thumbnailUrl || `https://storage.googleapis.com/movie-app-dev-bucket-veo/alekhine_thumbnail.jpg` || thumbnail
  };

  // Using fetchWithTrace:
  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects/${projectId}/artifacts`, {
      method: 'POST',
      credentials: 'include',
      body: payload, // fetchWithTrace handles stringify
    });
    const serverId = data?.artifact_id || data?._id || data?.id;
    if (serverId) payload._id = serverId;
  } catch (e) {
    console.warn("Artifact save to backend failed details:", e);
    throw new Error('Artifact save failed');
  }

  // 3) Keep a local cache (strip large data URIs to avoid QuotaExceededError)
  const local = ensureLocal(projectId);

  const cacheablePayload = { ...payload };
  if (cacheablePayload.inputImageUrl && cacheablePayload.inputImageUrl.startsWith('data:')) {
    cacheablePayload.inputImageUrl = ''; // Don't cache heavy base64
  }
  if (Array.isArray(cacheablePayload.inputImages)) {
    cacheablePayload.inputImages = cacheablePayload.inputImages.map(img =>
      (typeof img === 'string' && img.startsWith('data:')) ? '' : img
    );
  }

  local[type].unshift(cacheablePayload);
  try {
    localStorage.setItem(KEY(projectId), JSON.stringify(local));
  } catch (e) {
    console.warn('LocalStorage quota exceeded, skipping cache update', e);
  }

  return payload;
}

// add near other exports
export async function deleteArtifact(projectId, artifactId) {
  if (!projectId || !artifactId) throw new Error('deleteArtifact: missing ids');

  // try backend first
  try {
    await fetchWithTrace(
      `${API_URL}/artifacts/${artifactId}`,
      { method: 'DELETE', credentials: 'include' }
    );
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('deleteArtifact: backend delete failed, pruning local cache only', e);
  }

  // always prune local cache
  try {
    const k = KEY(projectId);
    const local = JSON.parse(localStorage.getItem(k) || '{"image":[],"video":[],"audio":[]}');
    for (const t of ['image', 'video', 'audio']) {
      local[t] = (local[t] || []).filter(a => (a._id || a.id) !== artifactId);
    }
    localStorage.setItem(k, JSON.stringify(local));
  } catch { }
}


export async function listRecent(projectId, kind, limit = 5) {
  if (!projectId) return [];
  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects/${projectId}/artifacts`, {
      credentials: 'include',
      // headers: { Accept: 'application/json' }, // fetchWithTrace might override/merge
    });

    const grouped =
      (data?.images || data?.videos || data?.audios)
        ? { image: data.images || [], video: data.videos || [], audio: data.audios || [] }
        : (data?.image || data?.video || data?.audio)
          ? data
          : Array.isArray(data) ? groupList(data) : { image: [], video: [], audio: [] };

    const arr = Array.isArray(grouped?.[kind]) ? grouped[kind] : [];
    return arr.slice(0, limit);
  } catch {
    const local = ensureLocal(projectId);
    return (local?.[kind] || []).slice(0, limit);
  }
}

export async function deleteArtifacts(projectId) {
  if (!projectId) return;
  try {
    await fetchWithTrace(`${API_URL}/projects/${projectId}/artifacts`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (e) {
    // backend might not support this yet; still clear cache locally
    if (process.env.NODE_ENV !== 'production') console.warn('deleteArtifacts fallback only (local cache cleared)', e);
  } finally {
    dropLocal(projectId);
  }
}